import { useState } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import AnimatedBackground from "@/components/AnimatedBackground";
import ScrollToTopButton from "@/components/ScrollToTopButton";
import SEO from "@/components/SEO";
import aivinityWebLogo from "@/assets/aivinity-web-logo.webp";
import aivinityVideoLogo from "@/assets/aivinity-video-logo.png";

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="glass rounded-2xl p-5 md:p-8 border border-border/50">
    <h2 className="font-serif text-heading text-foreground mb-4">{title}</h2>
    <div className="space-y-3 text-foreground/80 leading-relaxed">{children}</div>
  </div>
);

const AivinityPopup = ({
  logo,
  alt,
  label,
  description,
  url,
  accentColor,
}: {
  logo: string;
  alt: string;
  label: string;
  description: string;
  url: string;
  accentColor: string;
}) => {
  const [hovered, setHovered] = useState(false);

  return (
    <span
      className="relative inline-block"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => setHovered(!hovered)}
    >
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-foreground/5 backdrop-blur-sm border border-border/60 hover:border-primary/40 transition-all duration-300 hover:shadow-soft group cursor-pointer"
      >
        <img src={logo} alt={alt} className="h-4 w-auto" />
        <span className="text-xs font-medium text-foreground/70 group-hover:text-foreground transition-colors">{label}</span>
      </a>

      {hovered && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-64 p-4 rounded-xl bg-foreground/95 backdrop-blur-xl border border-background/20 shadow-2xl animate-scale-in origin-bottom z-50">
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 rotate-45 bg-foreground/95 border-r border-b border-background/20" />
          <div className="relative flex flex-col items-center gap-2 text-center">
            <img src={logo} alt={alt} className="h-8 w-auto" />
            <p className="text-background/90 text-sm font-medium">{description}</p>
            <div className="w-full h-px mt-1" style={{ background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)` }} />
            <span className="text-background/50 text-[10px]">{url.replace('https://', '')}</span>
          </div>
        </div>
      )}
    </span>
  );
};

const DatenschutzPage = () => {
  return (
    <main className="min-h-screen bg-background relative">
      <SEO
        title="Datenschutzerklärung | 22 Studios Nürnberg"
        description="Datenschutzerklärung von 22 Studios Nürnberg – Informationen zur Verarbeitung personenbezogener Daten gemäß DSGVO."
        canonical="https://22studios.de/nuernberg/datenschutz"
      />
      <AnimatedBackground />
      <Navigation />

      <section className="pt-24 pb-12 md:pt-32 md:pb-20 px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-serif text-display-lg md:text-display-xl mb-3 md:mb-4 text-foreground">
            Datenschutz
          </h1>
          <div className="h-1 w-20 bg-primary rounded-full mb-8 md:mb-12" />

          <div className="space-y-6 md:space-y-10">
            <Section title="1. Datenschutz auf einen Blick">
              <h3 className="font-serif text-subheading text-foreground">Allgemeine Hinweise</h3>
              <p>
                Die folgenden Hinweise geben einen einfachen Überblick darüber,
                was mit Ihren personenbezogenen Daten passiert, wenn Sie diese
                Website besuchen. Personenbezogene Daten sind alle Daten, mit
                denen Sie persönlich identifiziert werden können. Ausführliche
                Informationen zum Thema Datenschutz entnehmen Sie unserer
                nachfolgenden Datenschutzerklärung.
              </p>
              <h3 className="font-serif text-subheading text-foreground mt-6">Datenerfassung auf dieser Website</h3>
              <p>
                <strong>Wer ist verantwortlich für die Datenerfassung auf dieser Website?</strong><br />
                Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten
                können Sie dem Abschnitt „Verantwortliche Stelle" in dieser Datenschutzerklärung entnehmen.
              </p>
              <p>
                <strong>Wie erfassen wir Ihre Daten?</strong><br />
                Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen, z.&nbsp;B. durch Eingabe
                in ein Kontaktformular oder durch eine Terminbuchung. Andere Daten werden automatisch oder nach
                Ihrer Einwilligung beim Besuch der Website durch unsere IT-Systeme erfasst. Das sind vor allem
                technische Daten (z.&nbsp;B. Internetbrowser, Betriebssystem oder Uhrzeit des Seitenaufrufs).
              </p>
              <p>
                <strong>Wofür nutzen wir Ihre Daten?</strong><br />
                Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu gewährleisten.
                Andere Daten können zur Analyse Ihres Nutzerverhaltens verwendet werden.
              </p>
              <p>
                <strong>Welche Rechte haben Sie bezüglich Ihrer Daten?</strong><br />
                Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer
                gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung
                oder Löschung dieser Daten zu verlangen. Wenn Sie eine Einwilligung zur Datenverarbeitung erteilt
                haben, können Sie diese jederzeit für die Zukunft widerrufen. Außerdem haben Sie das Recht, unter
                bestimmten Umständen die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen.
                Des Weiteren steht Ihnen ein Beschwerderecht bei der zuständigen Aufsichtsbehörde zu.
              </p>
            </Section>

            <Section title="2. Verantwortliche Stelle">
              <p className="font-medium text-foreground">Vorname Nachname</p>
              <p>handelnd unter „22 Studios"</p>
              <p>Deidesheimer Str. 4</p>
              <p>90469 Nürnberg</p>
              <p>Deutschland</p>
              <p className="mt-2">
                Telefon:{" "}
                <a href="tel:+4915172530302" className="text-highlight hover:underline">
                  +49 151 7253 0302
                </a>
              </p>
              <p>
                E-Mail:{" "}
                <a href="mailto:info@22studios.de" className="text-highlight hover:underline">
                  info@22studios.de
                </a>
              </p>
              <p className="mt-3 text-foreground/60 text-sm">
                Verantwortliche Stelle ist die natürliche oder juristische Person, die allein oder gemeinsam mit
                anderen über die Zwecke und Mittel der Verarbeitung von personenbezogenen Daten entscheidet.
              </p>
            </Section>

            <Section title="3. Datenerfassung auf dieser Website">
              <h3 className="font-serif text-subheading text-foreground">Cookies</h3>
              <p>
                Unsere Website verwendet Cookies. Dabei handelt es sich um
                kleine Textdateien, die Ihr Webbrowser auf Ihrem Endgerät
                speichert. Cookies helfen uns dabei, unser Angebot
                nutzerfreundlicher und sicherer zu machen.
              </p>
              <p>
                Beim Besuch unserer Seite werden Sie über den Einsatz von Cookies
                informiert und können über den Cookie-Banner Ihre Einwilligung
                erteilen oder ablehnen. Sie können Ihren Browser so einstellen, dass Sie
                über das Setzen von Cookies informiert werden und Cookies nur im Einzelfall erlauben.
                Die Deaktivierung von Cookies kann die Funktionalität dieser Website einschränken.
              </p>
              <p>
                Cookies, die zur Durchführung des elektronischen Kommunikationsvorgangs, zur Bereitstellung
                bestimmter, von Ihnen erwünschter Funktionen oder zur Optimierung der Website erforderlich sind,
                werden auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO gespeichert, sofern keine andere Rechtsgrundlage
                angegeben wird. Der Websitebetreiber hat ein berechtigtes Interesse an der Speicherung von
                technisch notwendigen Cookies zur technisch fehlerfreien und optimierten Bereitstellung seiner Dienste.
              </p>

              <h3 className="font-serif text-subheading text-foreground mt-6">
                Kontaktformular / Anfrage
              </h3>
              <p>
                Wenn Sie uns per Kontaktformular Anfragen zukommen lassen,
                werden Ihre Angaben aus dem Anfrageformular inklusive der von
                Ihnen dort angegebenen Kontaktdaten (Name, E-Mail-Adresse,
                Telefonnummer, Nachricht) zwecks Bearbeitung der Anfrage und
                für den Fall von Anschlussfragen bei uns gespeichert. Diese
                Daten geben wir nicht ohne Ihre Einwilligung weiter.
              </p>
              <p>
                Die Verarbeitung dieser Daten erfolgt auf Grundlage von Art. 6
                Abs. 1 lit. b DSGVO, sofern Ihre Anfrage mit der Erfüllung
                eines Vertrags zusammenhängt oder zur Durchführung
                vorvertraglicher Maßnahmen erforderlich ist. In allen übrigen
                Fällen beruht die Verarbeitung auf unserem berechtigten
                Interesse an der effektiven Bearbeitung der an uns gerichteten
                Anfragen (Art. 6 Abs. 1 lit. f DSGVO) oder auf Ihrer
                Einwilligung (Art. 6 Abs. 1 lit. a DSGVO).
              </p>

              <h3 className="font-serif text-subheading text-foreground mt-6">
                Speicherdauer
              </h3>
              <p>
                Die von Ihnen im Kontaktformular eingegebenen Daten verbleiben
                bei uns, bis der Zweck der Speicherung entfällt (z.&nbsp;B. nach
                abgeschlossener Bearbeitung Ihrer Anfrage) oder Sie uns zur
                Löschung auffordern, sofern keine gesetzlichen
                Aufbewahrungspflichten entgegenstehen (z.&nbsp;B. handels- oder
                steuerrechtliche Aufbewahrungsfristen von bis zu 10 Jahren).
              </p>
            </Section>

            <Section title="4. Server-Logfiles">
              <p>
                Der Provider der Seiten erhebt und speichert automatisch
                Informationen in sogenannten Server-Logfiles, die Ihr Browser
                automatisch übermittelt. Dies sind:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Browsertyp und -version</li>
                <li>Verwendetes Betriebssystem</li>
                <li>Referrer-URL</li>
                <li>Hostname des zugreifenden Rechners</li>
                <li>IP-Adresse</li>
                <li>Uhrzeit der Serveranfrage</li>
              </ul>
              <p className="mt-3">
                Eine Zusammenführung dieser Daten mit anderen Datenquellen wird
                nicht vorgenommen. Die Erfassung dieser Daten erfolgt auf
                Grundlage von Art. 6 Abs. 1 lit. f DSGVO. Der
                Websitebetreiber hat ein berechtigtes Interesse an der
                technisch fehlerfreien Darstellung und der Optimierung seiner
                Website – hierzu müssen die Server-Logfiles erfasst werden.
              </p>
            </Section>

            <Section title="5. Hosting (IONOS)">
              <p>
                Diese Website wird bei <strong>IONOS SE</strong> (Elgendorfer
                Str. 57, 56410 Montabaur, Deutschland) gehostet. Die
                personenbezogenen Daten, die auf dieser Website erfasst werden,
                werden auf den Servern von IONOS gespeichert. Hierbei kann es
                sich v.&nbsp;a. um IP-Adressen, Kontaktanfragen, Meta- und
                Kommunikationsdaten, Vertragsdaten, Kontaktdaten, Namen,
                Websitezugriffe und sonstige Daten, die über eine Website
                generiert werden, handeln.
              </p>
              <p>
                Der Einsatz von IONOS erfolgt im Interesse einer sicheren,
                schnellen und effizienten Bereitstellung unseres
                Online-Angebots durch einen professionellen Anbieter (Art. 6
                Abs. 1 lit. f DSGVO). Sofern eine entsprechende Einwilligung
                abgefragt wurde, erfolgt die Verarbeitung ausschließlich auf
                Grundlage von Art. 6 Abs. 1 lit. a DSGVO. Wir haben einen
                Vertrag über Auftragsverarbeitung (AVV) mit IONOS geschlossen.
              </p>
              <p>
                Weitere Informationen finden Sie in der{" "}
                <a
                  href="https://www.ionos.de/terms-gtc/datenschutzerklaerung/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-highlight hover:underline"
                >
                  Datenschutzerklärung von IONOS
                </a>.
              </p>
            </Section>

            <Section title="6. Eingebettete Inhalte – Vimeo">
              <p>
                Auf dieser Website werden Videos des Anbieters <strong>Vimeo</strong> (Vimeo Inc., 555 West 18th Street,
                New York, New York 10011, USA) eingebettet. Wenn Sie eine Seite mit einem eingebetteten Vimeo-Video
                aufrufen, wird eine Verbindung zu den Servern von Vimeo hergestellt. Dabei wird dem Vimeo-Server
                mitgeteilt, welche Seite Sie besucht haben. Zudem erlangt Vimeo Ihre IP-Adresse.
              </p>
              <p>
                Wenn Sie in Ihrem Vimeo-Account eingeloggt sind, ermöglichen Sie Vimeo, Ihr Surfverhalten direkt
                Ihrem persönlichen Profil zuzuordnen. Dies können Sie verhindern, indem Sie sich aus Ihrem
                Vimeo-Account ausloggen.
              </p>
              <p>
                Die Nutzung von Vimeo erfolgt im Interesse einer ansprechenden Darstellung unserer Online-Angebote.
                Dies stellt ein berechtigtes Interesse im Sinne von Art. 6 Abs. 1 lit. f DSGVO dar. Sofern eine
                entsprechende Einwilligung abgefragt wurde, erfolgt die Verarbeitung ausschließlich auf Grundlage
                von Art. 6 Abs. 1 lit. a DSGVO.
              </p>
              <p>
                Die Datenübertragung in die USA wird auf die Standardvertragsklauseln der EU-Kommission gestützt.
                Details finden Sie in der{" "}
                <a
                  href="https://vimeo.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-highlight hover:underline"
                >
                  Datenschutzerklärung von Vimeo
                </a>.
              </p>
            </Section>

            <Section title="7. Google Fonts (lokales Hosting)">
              <p>
                Diese Website nutzt zur einheitlichen Darstellung von Schriftarten sogenannte Google Fonts.
                Die Schriftarten sind <strong>lokal auf unserem Server eingebunden</strong> und werden
                direkt von dieser Website ausgeliefert. Es findet <strong>keine Verbindung zu Servern
                von Google</strong> statt. Somit werden keine personenbezogenen Daten (insbesondere
                Ihre IP-Adresse) an Google übermittelt.
              </p>
              <p>
                Die Einbindung der Schriftarten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO.
                Der Websitebetreiber hat ein berechtigtes Interesse an der einheitlichen Darstellung
                des Schriftbildes auf seiner Website.
              </p>
            </Section>

            <Section title="8. Webentwicklung & Analyse (Lovable)">
              <p>
                Diese Website wurde mit der Plattform{" "}
                <strong>Lovable</strong> (Lovable Technologies AB, Schweden)
                erstellt. Im Rahmen des Betriebs der Website werden durch
                Lovable bestimmte technische Daten erfasst, darunter:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>IP-Adresse (anonymisiert)</li>
                <li>Browsertyp und -version</li>
                <li>Betriebssystem</li>
                <li>Referrer-URL</li>
                <li>Zeitpunkt des Seitenzugriffs</li>
                <li>Seitenaufrufe und Nutzungsstatistiken</li>
              </ul>
              <p className="mt-3">
                Diese Daten werden zur Sicherstellung des technischen Betriebs
                und zur Verbesserung der Plattform erhoben. Die Verarbeitung
                erfolgt auf Grundlage unseres berechtigten Interesses an einem
                sicheren und effizienten Webauftritt (Art. 6 Abs. 1 lit. f
                DSGVO). Weitere Informationen finden Sie in der{" "}
                <a
                  href="https://lovable.dev/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-highlight hover:underline"
                >
                  Datenschutzerklärung von Lovable
                </a>.
              </p>
            </Section>

            <Section title="9. Externe Dienste & Buchungsplattform">
              <h3 className="font-serif text-subheading text-foreground">
                Planity (Online-Buchung)
              </h3>
              <p>
                Für die Online-Terminbuchung nutzen wir den Dienst <strong>Planity</strong> (Planity SAS, Frankreich).
                Wenn Sie über Planity einen Termin buchen, werden Ihre dort eingegebenen Daten (Name, E-Mail,
                Telefonnummer, gewünschter Service) direkt von Planity verarbeitet. Die Verarbeitung erfolgt auf
                Grundlage von Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung bzw. vorvertragliche Maßnahmen).
              </p>
              <p>
                Planity speichert Ihre Daten in der EU. Weitere Informationen finden Sie in der
                Datenschutzerklärung von Planity.
              </p>
            </Section>

            <Section title="10. Foto- & Videomaterial / Webdesign">
              <p>
                Das auf dieser Website verwendete Foto- und Videomaterial wurde von einem externen
                Dienstleister erstellt. Das Webdesign und die technische Umsetzung dieser Website wurden
                ebenfalls von einem externen Dienstleister realisiert:
              </p>
              <div className="flex flex-wrap items-center gap-3 mt-4">
                <span className="text-foreground/70 text-sm">Foto & Video:</span>
                <AivinityPopup
                  logo={aivinityVideoLogo}
                  alt="aivinity video – Medienproduktion"
                  label="aivinity video"
                  description="Professionelle Foto- & Videoproduktion"
                  url="https://aivinity.de"
                  accentColor="#2d8a6e"
                />
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-3">
                <span className="text-foreground/70 text-sm">Webdesign:</span>
                <AivinityPopup
                  logo={aivinityWebLogo}
                  alt="aivinity web – Webdesign & Entwicklung"
                  label="aivinity web"
                  description="Webdesign & Entwicklung"
                  url="https://web.aivinity.de"
                  accentColor="#8b5cf6"
                />
              </div>
              <p className="mt-4">
                Im Rahmen der Erstellung des Bild- und Videomaterials sowie des Webdesigns werden keine
                personenbezogenen Daten der Websitebesucher an diese Dienstleister übermittelt. Die
                Zusammenarbeit erfolgt im Rahmen eines Werkvertrags. Eine Datenverarbeitung durch diese
                Dienstleister findet ausschließlich intern und nicht im Zusammenhang mit dem Betrieb
                dieser Website statt.
              </p>
            </Section>

            <Section title="11. Auftragsverarbeitung">
              <p>
                Wir haben mit unseren Dienstleistern, bei denen eine Auftragsverarbeitung stattfindet
                (IONOS – Hosting, Lovable – Webentwicklung, Planity – Online-Buchung),
                Auftragsverarbeitungsverträge (AVV) gemäß Art. 28 DSGVO abgeschlossen. Diese Verträge
                stellen sicher, dass die genannten Dienstleister die personenbezogenen Daten unserer
                Websitebesucher nur nach unseren Weisungen und unter Einhaltung der DSGVO verarbeiten.
              </p>
            </Section>

            <Section title="12. Ihre Rechte">
              <p>Sie haben jederzeit das Recht:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Auskunft</strong> über Ihre bei uns gespeicherten personenbezogenen Daten zu erhalten (Art. 15 DSGVO)</li>
                <li><strong>Berichtigung</strong> unrichtiger oder unvollständiger Daten zu verlangen (Art. 16 DSGVO)</li>
                <li><strong>Löschung</strong> Ihrer bei uns gespeicherten Daten zu verlangen (Art. 17 DSGVO)</li>
                <li><strong>Einschränkung</strong> der Verarbeitung Ihrer Daten zu verlangen (Art. 18 DSGVO)</li>
                <li><strong>Datenübertragbarkeit</strong> – Sie haben das Recht, Daten, die wir auf Grundlage Ihrer Einwilligung
                  oder in Erfüllung eines Vertrags automatisiert verarbeiten, in einem gängigen, maschinenlesbaren
                  Format zu erhalten (Art. 20 DSGVO)</li>
                <li><strong>Widerspruch</strong> gegen die Verarbeitung einzulegen, wenn die Verarbeitung auf Art. 6 Abs. 1 lit. e
                  oder f DSGVO beruht (Art. 21 DSGVO)</li>
                <li><strong>Beschwerde</strong> bei einer Aufsichtsbehörde einzureichen (Art. 77 DSGVO)</li>
              </ul>
              <p className="mt-3">
                Zuständige Aufsichtsbehörde: <strong>Bayerisches Landesamt für
                Datenschutzaufsicht (BayLDA)</strong>, Promenade 18, 91522 Ansbach,{" "}
                <a
                  href="https://www.lda.bayern.de"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-highlight hover:underline"
                >
                  www.lda.bayern.de
                </a>.
              </p>
            </Section>

            <Section title="13. Widerruf Ihrer Einwilligung zur Datenverarbeitung">
              <p>
                Viele Datenverarbeitungsvorgänge sind nur mit Ihrer
                ausdrücklichen Einwilligung möglich. Sie können eine bereits
                erteilte Einwilligung jederzeit widerrufen. Die Rechtmäßigkeit
                der bis zum Widerruf erfolgten Datenverarbeitung bleibt vom
                Widerruf unberührt. Das Widerrufsrecht können Sie jederzeit per
                E-Mail an{" "}
                <a href="mailto:info@22studios.de" className="text-highlight hover:underline">
                  info@22studios.de
                </a>{" "}
                ausüben.
              </p>
            </Section>

            <Section title="14. Widerspruchsrecht (Art. 21 DSGVO)">
              <div className="p-4 rounded-xl bg-foreground/5 border border-border/60">
                <p className="font-medium text-foreground">
                  Wenn die Datenverarbeitung auf Grundlage von Art. 6 Abs. 1 lit. e oder f DSGVO erfolgt,
                  haben Sie jederzeit das Recht, aus Gründen, die sich aus Ihrer besonderen Situation ergeben,
                  gegen die Verarbeitung Ihrer personenbezogenen Daten Widerspruch einzulegen. Die jeweilige
                  Rechtsgrundlage, auf der eine Verarbeitung beruht, entnehmen Sie dieser Datenschutzerklärung.
                  Wenn Sie Widerspruch einlegen, werden wir Ihre betroffenen personenbezogenen Daten nicht mehr
                  verarbeiten, es sei denn, wir können zwingende schutzwürdige Gründe für die Verarbeitung
                  nachweisen, die Ihre Interessen, Rechte und Freiheiten überwiegen, oder die Verarbeitung dient
                  der Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen.
                </p>
              </div>
            </Section>

            <Section title="15. SSL- bzw. TLS-Verschlüsselung">
              <p>
                Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der
                Übertragung vertraulicher Inhalte eine SSL- bzw.
                TLS-Verschlüsselung. Eine verschlüsselte Verbindung erkennen
                Sie daran, dass die Adresszeile des Browsers von „http://" auf
                „https://" wechselt und an dem Schloss-Symbol in Ihrer
                Browserzeile. Wenn die SSL- bzw. TLS-Verschlüsselung aktiviert
                ist, können die Daten, die Sie an uns übermitteln, nicht von
                Dritten mitgelesen werden.
              </p>
            </Section>

            <Section title="16. Aktualität und Änderung dieser Datenschutzerklärung">
              <p>
                Diese Datenschutzerklärung ist aktuell gültig und hat den Stand
                April 2026. Durch die Weiterentwicklung unserer Website oder
                aufgrund geänderter gesetzlicher bzw. behördlicher Vorgaben kann
                es notwendig werden, diese Datenschutzerklärung zu ändern.
                Die jeweils aktuelle Datenschutzerklärung kann jederzeit auf
                dieser Website von Ihnen abgerufen und ausgedruckt werden.
              </p>
            </Section>
          </div>
        </div>
      </section>

      <Footer />
      <ScrollToTopButton />
    </main>
  );
};

export default DatenschutzPage;
